// src/utils/searchIndex.js

import router from "@/router";
import testGeneMap from "@/utils/testGeneMap";

/**
 * 当前上线版本：
 * 1. 支持 test name 搜索
 * 2. 支持 disease name / category / keyword 搜索
 * 3. 支持点击搜索结果跳转对应页面
 * 4. 暂不显示 gene 搜索文案，避免客户误解
 *
 * 后续如果要支持 gene 搜索，只需要补充 geneMap。
 */

const geneMap = testGeneMap;

const testMenuKeywords = [
    "Test Menu"
];

const howToOrderKeywords = [
    "How To Order"
];

const resourcesKeywords = [
    "Resources"
];

const industryResearchKeywords = [
    "Industry and Research Services",
    "Industry Research Services",
    "Industry Services",
    "Research Services"
];

const sampleRequirementKeywords = [
    "Sample Requirements and Shipping Instructions",
    "Specimen Requirements and Shipping Instructions",
    "Sample and Shipping Requirements",
    "Sample Requirements",
    "Shipping Requirements",
    "Specimen Requirements",
    "Accepted Sample Types",
    "Sample Types",
    "Whole Blood",
    "Peripheral Blood",
    "Buccal Swab",
    "Saliva",
    "Extracted DNA",
    "Cultured Cells",
    "Cultured Cells pre or postnatal",
    "Amniotic Fluid",
    "Direct Amniotic Fluid",
    "Skin Biopsy",
    "Skin Biopsy or Tissue",
    "Tissue",
    "Formalin-Fixed Paraffin-Embedded",
    "FFPE"
];

const clinicalCategoryItems = [
    { title: "Audiology", path: "/test/category/audiology" },
    { title: "Cardiology", path: "/test/category/cardiology" },
    { title: "Connective Tissue and Skin", path: "/test/category/connective-tissue-and-skin" },
    { title: "Endocrinology", path: "/test/category/endocrinology" },
    { title: "Gastroenterology", path: "/test/category/gastroenterology" },
    { title: "Genome-Wide CNV Analysis", path: "/test/category/genome-wide-cnv-analysis" },
    { title: "Healthy Screening", path: "/test/category/healthy-screening" },
    { title: "Hematology", path: "/test/category/hematology" },
    { title: "Immunology", path: "/test/category/immunology" },
    { title: "Intellectual Disability", path: "/test/category/intellectual-disability" },
    { title: "Metabolic", path: "/test/category/metabolic" },
    { title: "Mitochondrial Genome Sequencing", path: "/test/category/mitochondrial-genome-sequencing" },
    { title: "Nephrology", path: "/test/category/nephrology" },
    { title: "Neurology", path: "/test/category/neurology" },
    { title: "Oncology", path: "/test/category/oncology" },
    { title: "Ophthalmology", path: "/test/category/ophthalmology" },
    { title: "Overgrowth Disorders", path: "/test/category/overgrowth-disorders" },
    { title: "Pharmacogenomics", path: "/test/category/pharmacogenomics" },
    { title: "Pulmonology", path: "/test/category/pulmonology" },
    { title: "Repeat Expansion Disorders", path: "/test/category/repeat-expansion-disorders" },
    { title: "Skeletal and Dental", path: "/test/category/skeletal-and-dental" }
];

const keywordMap = {
    "/home": ["home", "homepage"],

    "/clinical-case-examples": [
        "Clinical Case Examples",
        "Clinical Case Studies",
        "Case Examples",
        "Case Studies",
        "Featured Case Example",
        "Patient",
        "Testing Results",
        "Key Differentiator",
        "AiLife WGS and WES TRIO",
        "Repeat Expansion Targeted Panel",
        "Oxford Nanopore",
        "Long Read Sequencing",
        "Dystonia",
        "Global Developmental Delay",
        "Epilepsy",
        "Abnormal EEG",
        "Abnormal Brain MRI",
        "Failure to Thrive",
        "Spastic Quadriplegia",
        "UGT1A1",
        "PRORP",
        "ARX",
        "Developmental and Epileptic Encephalopathy",
        "SNVs",
        "Small Indels",
        "CNVs",
        "SVs",
        "Mitochondrial Variants",
        "Uniparental Disomy",
        "UPD",
        "Repeat Expansions",
        "Spinocerebellar Ataxia",
        "GAA Repeats",
        "FGF14",
        "SCA27",
        "Repeat Sizing",
        "Repeat Loci",
        ...resourcesKeywords
    ],

    // ============ ORDER ============
    "/order/testing": [
        "How To Order Testing",
        ...howToOrderKeywords,
        "How Order Testing",
        "Order Testing",
        "Testing",
        "Order",
        "Ordering Made Easy",
        "Online Ordering",
        "Online Ordering Recommended",
        "Provider Portal",
        "Create An Order",
        "Submit Order",
        "Electronic Submission Alternative",
        "TRF",
        "Test Requisition Form",
        "Consent",
        "Consent Form",
        "Genetic Testing Consent",
        "Genetic Testing Consent Form",
        "TRF and Consent",
        "AiLife TRF and Consent",
        "US Version",
        "Canadian Version",
        "Drop Files Here",
        "Click to Upload",
        "Paper-based Ordering Alternative",
        "Paper Based Ordering Alternative",
        "Collect the Sample",
        "Sample Collection and Shipping Requirements",
        "Sample Collection Kit Form",
        "Provider Portal Account",
        "Preparing the Shipment",
        "Shipping Instructions",
        "Biohazard Bag",
        "Receiving Department",
        "Accessioned"
    ],
    "/order/kit": [
        // "Sample Collection Kit Request Form",
        // "Sample Collection Kit",
        // "Kit Request",
        // "Order Kit"
    ],
    "/order/specimen": [
        ...sampleRequirementKeywords,
        ...howToOrderKeywords,
        "Specimen",
        "Unique Patient Identifiers",
        "Date of Birth",
        "Uncleaned Chorionic Villus Sampling",
        "Chorionic Villus Sampling",
        "CVS",
        "EDTA",
        "DNA Genotek",
        "OCD-100",
        "CLIA-certified",
        "CLIA Certified",
        "CAP",
        "CMS",
        "Cold Pack",
        "T25 Flasks",
        "80% Confluency",
        "Room Temperature",
        "FFPE Block",
        "Unstained Slides",
        "Fetal Tissue",
        "Biohazard Bag",
        "Cardboard Box",
        "Overnight Courier",
        "Receiving Department"
    ],
    "/order/billing": [
        "Billing and Insurance",
        ...howToOrderKeywords,
        "Billing",
        "Insurance",
        "Billing Insurance",
        "Payment",
        "Payment Options",
        "Rare Disease",
        "Institutional Billing",
        "Fair Pricing",
        "Contracts",
        "Insurance Billing",
        "Insurance Claim",
        "Referral Form",
        "Medical Necessity",
        "Clinical Notes",
        "Insurance Card",
        "Test Requisition Form",
        "ICD-10",
        "Diagnosis Code",
        "Coverage",
        "Genetic Testing",
        "Co-pay",
        "Co Insurance",
        "Co-insurance",
        "Deductible",
        "Benefits Investigation",
        "BI",
        "Benefits Investigation Form",
        "In-Network",
        "Private Insurance Carriers",
        "Contracted Medicaid Plans",
        "Contracted Medicare and Medicaid Plans",
        "Medicaid",
        "Medicare",
        "BCBS",
        "Molina",
        "Patient Self-Pay",
        "Self Pay",
        "Self-Pay Pricing"
    ],
    "/order/policies": [
        "Lab Policies",
        ...howToOrderKeywords,
        "Policies",
        "Laboratory Policies",
        "Family Variant Testing",
        "FVT",
        "Parental Testing Policy",
        "Variant of Uncertain Significance",
        "VUS",
        "VUS Reclassification",
        "Reclassification Policy",
        "Data Reanalysis Policy",
        "Sample and Data Storage and Retention Policy",
        "Storage and Retention Policy",
        "Test Cancelation Policy",
        "Proband",
        "No Cost Parental Testing",
        "Siblings",
        "Extended Family Members",
        "Ad Hoc Variant Review",
        "Medical Evidence",
        "Likely Benign",
        "Benign",
        "Likely Pathogenic",
        "Pathogenic",
        "Amended Report",
        "Patient Management",
        "Raw Data",
        "Whole Genome Sequencing",
        "Whole Exome Sequencing",
        "Reanalysis",
        "New Organ System Findings",
        "Inconclusive",
        "Negative",
        "Sample IDs",
        "Accessioning",
        "DNA Isolation",
        "Residual DNA",
        "Clinical Reports",
        "Cancelation",
        "Cancel",
        "Processing",
        "Invoice"
    ],
    "/order/forms": [
        "Forms",
        ...howToOrderKeywords,
        "Test Requisition and Consent Form",
        "Test Requisition and Consent Form US",
        "Test Requisition and Consent Form Canada",
        "Consent Form",
        "F8 Inversions Form",
        "Benefits Investigation Form"
    ],

    // ============ TEST BASIC ============
    "/test/WholeGenomeSequencingProbandOnly": [
        ...testMenuKeywords,
        "WGS",
        "Whole Genome Sequencing",
        "Genome Sequencing",
        "Proband Only",
        "Genome Sequencing, DUO",
        "DUO",
        "Genome Sequencing, TRIO",
        "TRIO",
        "Genome Sequencing, QUAD",
        "QUAD",
        "WGS Plus",
        "Pharmacogenomic",
        "WGS Expansion",
        "Repeat Expansion Disorder",
        "Repeat",
        "WGS Apex",
        "Long Read Sequencing"
    ],
    "/test/WholeExomeSequencingProbandOnly": [
        ...testMenuKeywords,
        "WES",
        "Whole Exome Sequencing",
        "Exome Sequencing",
        "Proband Only",
        "Exome Sequencing, DUO",
        "DUO",
        "Exome Sequencing, TRIO",
        "TRIO",
        "Exome Sequencing, QUAD",
        "QUAD",
        "WES Complete",
        "Mitochondrial Genome Sequencing",
        "WES Expansion",
        "Repeat Expansion Disorder",
        "Repeat",
        "WES Max",
        "Long Read Sequencing"
    ],
    "/test/Repeat Expansion Disorders": [
        ...testMenuKeywords,
        "Repeat Expansion Disorders",
        "Repeat Expansion",
        "Expansion Disorders",
        "Neurological Disorders",
        "Ataxia",
        "Neuromscular Disorders",
        "Neuromuscular Disorders",
        "Congenital Disorders",
        "Development Delay",
        "Movement Disorders",
        "Long Read Sequencing"
    ],
    "/test/Genome-Wide CNV Analysis": [
        ...testMenuKeywords,
        "Genome-Wide CNV Analysis",
        "CNV",
        "Copy Number Variant",
        "Copy Number Variation"
    ],
    "/test/gene": [
        ...testMenuKeywords,
        "GENE PANELS",
        "Gene Panel",
        "Genetic Testing",
        "Panels"
    ],

    // ============ AUDIOLOGY ============
    "/test/HearingLoss": [
        ...testMenuKeywords,
        "Hearing Loss",
        "Hearing Loss Panel",
        "Audiology",
        "Deafness"
    ],

    // ============ CARDIOLOGY ============
    "/test/Amyloidosis Panel": [
        "Amyloidosis",
        "Amyloidosis Panel",
        "Amyloid",
        "Hereditary Amyloidosis",
        "Transthyretin Amyloidosis",
        "Apolipoprotein AI Amyloidosis",
        "Apolipoprotein AII Amyloidosis",
        "Cystatin C Amyloidosis",
        "Fibrinogen Amyloidosis",
        "Gelsolin Amyloidosis",
        "Lysozyme Amyloidosis",
        "TTR",
        "Cardiology"
    ],
    "/test/AortopathyDisorders": [
        "Aortopathy",
        "Connective Tissue",
        "Aortopathy Disorders",
        "Cardiology"
    ],
    "/test/Arrhythmias": [
        "Arrhythmias",
        "Arrhythmia",
        "Heart Rhythm",
        "Cardiology"
    ],
    "/test/Cardiomyopathy": [
        "Cardiomyopathy",
        "Cardiomyopathy Panel",
        "Heart Disease",
        "Cardiology"
    ],
    "/test/Congenital Heart Defect": [
        "Congenital Heart Defect",
        "Heart Defect",
        "Cardiology"
    ],
    "/test/Familial Hypercholesterolemia": [
        "Familial Hypercholesterolemia",
        "Hypercholesterolemia",
        "Cholesterol",
        "Cardiology"
    ],
    "/test/Hyperlipidemia Panel": [
        "Hyperlipidemia",
        "Hyperlipidemia Panel",
        "High Lipids",
        "High Levels of Lipids",
        "Apolipoprotein C-III Deficiency",
        "Familial Hypercholesterolemia",
        "Lipoprotein Lipase Deficiency",
        "Primary Hypoalphalipoproteinemia",
        "Sitosterolemia",
        "Cardiology"
    ],
    "/test/Noonan Syndrome": [
        "Noonan Syndrome",
        "RASopathy",
        "Cardiology"
    ],
    "/test/Hypertrophic Cardiomyopathy": [
        "Hypertrophic Cardiomyopathy",
        "HCM",
        "Cardiology"
    ],
    "/test/Dilated Cardiomyopathy": [
        "Dilated Cardiomyopathy",
        "DCM",
        "Cardiology"
    ],
    "/test/Arrhythmogenic Right Ventricular/Dysplasia": [
        "Arrhythmogenic Right Ventricular",
        "Dysplasia",
        "ARVC",
        "ARVD",
        "Cardiology"
    ],
    "/test/Left Ventricular Noncompaction": [
        "Left Ventricular Noncompaction",
        "LVNC",
        "Cardiology"
    ],
    "/test/Noonan Syndrome And Other RASopathies": [
        "Noonan Syndrome",
        "RASopathies",
        "RASopathy",
        "Cardiology"
    ],
    "/test/Brugada Syndrome": [
        "Brugada Syndrome",
        "Brugada",
        "Cardiology"
    ],
    "/test/Catecholaminergic Polymorphic Ventricular Tachycardia": [
        "Catecholaminergic Polymorphic Ventricular Tachycardia",
        "CPVT",
        "Ventricular Tachycardia",
        "Cardiology"
    ],
    "/test/Long QT Syndrome": [
        "Long QT Syndrome",
        "LQTS",
        "QT",
        "Cardiology",
        "Arrhythmia"
    ],
    "/test/Short QT Syndrome": [
        "Short QT Syndrome",
        "SQTS",
        "QT",
        "Cardiology",
        "Arrhythmia"
    ],

    // ============ CONNECTIVE TISSUE ============
    "/test/Aortopathy and Connective Tissue Disorders": [
        "Aortopathy",
        "Connective Tissue Disorders",
        "Connective Tissue",
        "Skin"
    ],
    "/test/Connective Tissue Disorders": [
        "Connective Tissue Disorders",
        "Connective Tissue",
        "Skin"
    ],

    // ============ ENDOCRINOLOGY ============
    "/test/Hypoglycemia Panel": [
        "Hypoglycemia",
        "Hypoglycemia Panel",
        "Low Blood Glucose",
        "Endocrinology"
    ],
    "/test/Comprehensive Diabetes and Obesity": [
        "Comprehensive Diabetes and Obesity",
        "Diabetes",
        "Obesity",
        "Endocrinology"
    ],
    "/test/Familial Hyperparathyroidism or Hypocalciuric Hypercalcemia Panel": [
        "Familial Hyperparathyroidism or Hypocalciuric Hypercalcemia",
        "Familial Hyperparathyroidism or Hypocalciuric Hypercalcemia Panel",
        "Familial Hyperparathyroidism",
        "Hypocalciuric Hypercalcemia",
        "Hyperparathyroidism",
        "Hypercalcemia",
        "High Blood Calcium",
        "Parathyroid Hormone",
        "PTH",
        "Endocrinology"
    ],
    "/test/Hyperinsulinism Panel": [
        "Hyperinsulinism",
        "Hyperinsulinism Panel",
        "Congenital Hyperinsulinism",
        "Persistent Hypoglycemia",
        "Hypoglycemia",
        "Inappropriate Insulin Secretion",
        "Kabuki Syndrome",
        "Congenital Disorders of Glycosylation",
        "Endocrinology"
    ],
    "/test/Maturity-Onset Diabetes of the Young": [
        "Maturity-Onset Diabetes of the Young",
        "MODY",
        "Diabetes",
        "Endocrinology"
    ],
    "/test/Neonatal Diabetes Mellitus": [
        "Neonatal Diabetes Mellitus",
        "Neonatal Diabetes",
        "Diabetes Mellitus",
        "Diabetes",
        "Endocrinology"
    ],
    "/test/Inherited Obesity": [
        "Inherited Obesity",
        "Obesity",
        "Endocrinology"
    ],

    // ============ GASTROENTEROLOGY ============
    "/test/Hereditary Liver Disease": [
        "Hereditary Liver Disease",
        "Liver Disease",
        "Gastroenterology",
        "Liver"
    ],

    // ============ HEALTHY SCREENING ============
    "/test/Ashkenazi Jewish Carrier Screening Panel": [
        "Ashkenazi Jewish Carrier Screening Panel",
        "Ashkenazi Jewish Screening Panel",
        "Carrier Screening",
        "Healthy Screening"
    ],
    "/test/Healthy Screening Basic": [
        "Healthy Screening Basic",
        "Healthy Screening",
        "Basic Panel",
        "Screening"
    ],
    "/test/Healthy Screening Expanded": [
        "Healthy Screening Expanded",
        "Healthy Screening",
        "Expanded Panel",
        "Screening"
    ],

    // ============ HEMATOLOGY ============
    "/test/Anemia": [
        "Anemia",
        "Hematology"
    ],
    "/test/Bleeding Disorders": [
        "Bleeding Disorders",
        "Bleeding",
        "Hematology"
    ],
    "/test/Bone Marrow Failure Disorders": [
        "Bone Marrow Failure Disorders",
        "Bone Marrow Failure",
        "Hematology"
    ],
    "/test/Fanconi Anemia": [
        "Fanconi Anemia",
        "Anemia",
        "Hematology"
    ],
    "/test/F8 Inversion": [
        "F8 Inversions",
        "F8",
        "Hemophilia",
        "Hematology"
    ],
    "/test/Platelet Disease": [
        "Platelet Disease",
        "Platelet",
        "Hematology"
    ],

    // ============ IMMUNOLOGY ============
    "/test/Autoimmunity and Autoinflammatory Disease": [
        "Autoimmunity",
        "Autoinflammatory Disease",
        "Autoimmune",
        "Immunology"
    ],
    "/test/Hemophagocytic Lymphohistiocytosis Panel": [
        "Hemophagocytic Lymphohistiocytosis",
        "Hemophagocytic Lymphohistiocytosis Panel",
        "HLH",
        "Active Immune Cells",
        "Immune Cells",
        "Immunology"
    ],
    "/test/Neutropenia Panel": [
        "Neutropenia",
        "Neutropenia Panel",
        "Low Neutrophils",
        "Neutrophils",
        "White Blood Cell",
        "Bacterial Infections",
        "Fungal Infections",
        "Immunology"
    ],
    "/test/Primary Immunodeficiencies and Cytopenia": [
        "Primary Immunodeficiencies",
        "Cytopenia",
        "Immunodeficiency",
        "Immunology"
    ],

    // ============ INTELLECTUAL DISABILITY ============
    "/test/Intellectual Disability Panel": [
        "Intellectual Disability",
        "Intellectual Disability Panel",
        "Neurodevelopmental Conditions",
        "Cognitive Functioning",
        "Adaptive Functioning",
        "Developmental Disorder",
        "Developmental Delay"
    ],

    // ============ METABOLIC ============
    "/test/Lysosomal Storage Disease": [
        "Lysosomal Storage Disease",
        "Lysosomal Storage Disorders",
        "Metabolic"
    ],

    // ============ NEPHROLOGY ============
    "/test/Comprehensive Renal Disease": [
        "Comprehensive Renal Disease",
        "Renal Disease",
        "Kidney Disease",
        "Nephrology"
    ],

    // ============ NEUROLOGY ============
    "/test/Amyotrophic Lateral Sclerosis Panel": [
        "Amyotrophic Lateral Sclerosis",
        "Amyotrophic Lateral Sclerosis Panel",
        "ALS",
        "Neurology"
    ],
    "/test/Cerebral Cavernous Malformations Panel": [
        "Cerebral Cavernous Malformations",
        "Cerebral Cavernous Malformations Panel",
        "Neurology"
    ],
    "/test/Dystonia Panel": [
        "Dystonia",
        "Dystonia Panel",
        "Movement Disorder",
        "Muscle Contractions",
        "Abnormal Movements",
        "Postures",
        "Neurology"
    ],
    "/test/Microcephaly Panel": [
        "Microcephaly",
        "Microcephaly Panel",
        "Small Head",
        "Head Circumference",
        "Brain Growth",
        "Structural Brain Anomalies",
        "Neurology"
    ],
    "/test/Leukodystrophy and Leukoencephalopathy Panel": [
        "Leukodystrophy",
        "Leukoencephalopathy",
        "Leukodystrophy and Leukoencephalopathy Panel",
        "Neurology"
    ],
    "/test/Muscular Dystrophy Panel": [
        "Muscular Dystrophy",
        "Muscular Dystrophy Panel",
        "Neurology"
    ],
    "/test/Myopathy Panel": [
        "Myopathy",
        "Myopathy Panel",
        "Neurology"
    ],
    "/test/Parkinson’s Disease, Alzheimer’s Disease, and Dementia": [
        "Parkinson",
        "Parkinson’s Disease",
        "Alzheimer",
        "Alzheimer’s Disease",
        "Dementia",
        "Neurology"
    ],
    "/test/Paroxysmal Dyskinesia Panel": [
        "Paroxysmal Dyskinesia",
        "Paroxysmal Dyskinesia Panel",
        "Neurology"
    ],
    "/test/Seizure Panel": [
        "Seizure",
        "Seizure Panel",
        "Epilepsy",
        "Seizure Disorder",
        "Neurology"
    ],

    // ============ ONCOLOGY ============
    "/test/Hereditary Cancer": [
        "Hereditary Cancer",
        "Cancer",
        "Oncology",
        "Tumor"
    ],

    // ============ OPHTHALMOLOGY ============
    "/test/Corneal Dystrophy Panel": [
        "Corneal Dystrophy",
        "Corneal Dystrophy Panel",
        "Ophthalmology"
    ],
    "/test/Comprehensive Eye Disorders": [
        "Comprehensive Eye Disorders",
        "Eye Disorders",
        "Ophthalmology",
        "Eye"
    ],

    // ============ OVERGROWTH DISORDERS ============
    "/test/Macrocephaly Panel": [
        "Macrocephaly Panel",
        "Macrocephaly",
        "Overgrowth Disorders",
        "Overgrowth",
        "Large Head",
        "Head Circumference",
        "Megalencephaly"
    ],

    // ============ PHARMACOGENOMIC ============
    "/test/Pharmacogenomic Panel": [
        "Pharmacogenomic",
        "Pharmacogenomics",
        "PGx",
        "Medication",
        "Drug Response"
    ],

    // ============ PULMONOLOGY ============
    "/test/Comprehensive Ciliopathy": [
        "Comprehensive Ciliopathy",
        "Ciliopathy",
        "Pulmonology"
    ],
    "/test/Heterotaxy": [
        "Heterotaxy",
        "Pulmonology"
    ],
    "/test/Interstitial Lung Disease": [
        "Interstitial Lung Disease",
        "Pulmonary Fibrosis",
        "Surfactant Dysfunction",
        "Pulmonology",
        "Lung Disease"
    ],
    "/test/Primary Ciliary Dyskinesia Panel": [
        "Primary Ciliary Dyskinesia",
        "Primary Ciliary Dyskinesia Panel",
        "Ciliary Dyskinesia",
        "Ciliary Dysfunction",
        "Motile Ciliary Dysfunction",
        "Respiratory Disease",
        "Respiratory Failure",
        "Sinus Infection",
        "Ear Infection",
        "Infertility",
        "Pulmonology"
    ],

    // ============ SKELETAL ============
    "/test/Bone Fracture Panel": [
        "Bone Fracture",
        "Bone Fracture Panel",
        "Bone Strength",
        "Bone Mineralization",
        "Osteogenesis Imperfecta",
        "Hypophosphatasia",
        "Hypophosphatemic Rickets",
        "Sclerosing Bone Dysplasias",
        "Paget Disease of Bone",
        "Skeletal",
        "Dental"
    ],
    "/test/Osteogenesis Imperfecta": [
        "Osteogenesis Imperfecta",
        "Skeletal",
        "Dental",
        "Bone"
    ],
    "/test/Short Stature Panel": [
        "Short Stature",
        "Short Stature Panel",
        "Endocrine Abnormalities",
        "Proportionate Short Stature",
        "Disproportionate Short Stature",
        "HOXD13",
        "Skeletal",
        "Dental"
    ],
    "/test/Skeletal Dysplasia": [
        "Skeletal Dysplasia",
        "Skeletal",
        "Dental",
        "Bone"
    ],

    // ============ OTHER ============
    "/test/Mitochondrial Genome Sequencing": [
        "Mitochondrial Genome Sequencing",
        "Mitochondrial",
        "Mitochondria"
    ],

    // ============ SERVICES ============
    "/Services/AilisNGS": [
        ...industryResearchKeywords,
        "AilisNGS",
        "NGS",
        "Services",
        "End-to-End Laboratory Information and NGS Interpretation System",
        "Laboratory Information",
        "NGS Interpretation System",
        "Clinical Molecular Geneticists",
        "Bioinformaticians",
        "Researchers",
        "Laboratorians",
        "Hospitals",
        "Bioinformatics Analysis",
        "Interpretation",
        "Annotation",
        "Curation",
        "Variants",
        "Patient Sequences",
        "Clinical Workflow",
        "Publicly Available Databases",
        "Proprietary Knowledgebases",
        "Key Product Highlights",
        "SNV",
        "Single Nucleotide Variant",
        "SV",
        "Structural Variant",
        "CNV",
        "Copy Number Variants",
        "Small Indel",
        "Variant Calls",
        "Annotated Variants",
        "BAM Data",
        "Variant Filtering",
        "Variant Prioritization",
        "Auto-scoring",
        "ACMG",
        "Variant Classification",
        "FASTQ",
        "BAM",
        "VCF",
        "Batch Uploaded",
        "Automated Report Drafting",
        "Report Table Generation",
        "Electronic Signature",
        "Query Tools",
        "Statistics",
        "Turnaround Time",
        "TAT",
        "Positive Hit Rates",
        "Human Phenotype Ontology",
        "HPO Tool",
        "Data Security",
        "Amazon Web Services",
        "AWS",
        "HIPAA-compliant",
        "HiTrust-certified",
        "Demo"
    ],
    "/Services/BioPharmaPartnerships": [
        ...industryResearchKeywords,
        "BioPharma Partnerships",
        "BioPharma",
        "Partnerships",
        "Services",
        "Precision Medicine",
        "Industry",
        "Sponsored Testing Programs",
        "Sponsored Testing",
        "No Cost Genetic Testing",
        "Diagnostic Odyssey",
        "Eligible Patients",
        "Healthcare Providers",
        "Regulatory Environment",
        "Compliant Sponsored Testing Programs",
        "Clinical Focuses",
        "Industry Partners",
        "Scopes of Work",
        "Clinical Trials and Research",
        "Clinical Trials",
        "Research",
        "Therapeutic Interventions",
        "Rare Disease",
        "Phase I-IV Clinical Trials",
        "Clinical Trial Samples",
        "Sample Chain-of-Custody",
        "Sample Stability",
        "Residual DNA",
        "Sponsors",
        "Primary Investigators",
        "Sites",
        "Turnaround Time",
        "Subject Enrollment Windows",
        "Other Industry Collaborations",
        "Commercial Laboratories",
        "Non-profit Organizations",
        "Patient Advocacy Groups",
        "Comprehensive Solutions",
        "MAGEL2",
        "Schaaf-Yang Syndrome"
    ],

    // ============ PRODUCTS ============
    "/products": [
        "Products",
        "Services",
        "Products and Services"
    ],
    "/products/Brochures": [
        "Brochures",
        "Products Brochures",
        "Product Brochures",
        "Featured Brochure",
        "Official PDF",
        "Download Now",
        "Downloads",
        "All Brochures",
        "PDF",
        "AiLife Rapid Whole Genome Sequencing",
        "Rapid Whole Genome Sequencing",
        "Rapid WGS",
        "AiLife Rapid Whole Exome Sequencing",
        "Rapid Whole Exome Sequencing",
        "Rapid WES",
        "AiLife Whole Exome Sequencing",
        "Whole Exome Sequencing",
        "WES",
        "AiLife Health Screening",
        "Health Screening",
        "AilisNGS",
        "Genome-wide CNV Analysis",
        "Genome Wide CNV Analysis",
        "CNV Analysis",
        ...resourcesKeywords
    ],
    "/products/VariantInterpretationService": [
        "Variant Interpretation Service",
        "Variant Interpretation",
        "Products",
        "Variant Review",
        "Variant Reclassification",
        "Re-classification",
        "Reclassification",
        "No-cost Service",
        "Rolling Basis",
        "Turnaround Time",
        "Provider Portal",
        "Variant Interpretation Service Form",
        "Requests",
        "Criteria",
        "Detected",
        "AiLife",
        "6 Months",
        "New Evidence",
        "Published",
        "Variants of Uncertain Significance",
        "VUS",
        "Patient Management",
        ...resourcesKeywords
    ],
    "/products/RequestGeneticistConsultation": [
        "Request a Geneticist Consultation",
        "Request Geneticist Consultation",
        "Geneticist Consultation",
        "Consultation",
        "Request Consultation",
        "ABMGG-Certified Laboratory Geneticist",
        "ABMGG Certified Laboratory Geneticist",
        "Laboratory Geneticist",
        "Test Menus",
        "Healthcare Providers",
        "Patients",
        "Single Variant Testing",
        "Comprehensive Panels",
        "Exome",
        "Genome",
        "RNA",
        "Long Read Sequencing",
        "Testing Solution",
        "Rare Disease",
        "Best Next Step",
        "Schedule a Consultation",
        ...resourcesKeywords
    ],
    "/products/UnderstandingResults": [
        "Understanding Results",
        "Results",
        "Genetic Testing",
        "Result Interpretation",
        "Limitations",
        "Follow-up Support",
        "High-complexity Testing",
        "Sequencing Technology",
        "Disease Mechanisms",
        "Rare Disease Population",
        "Phenotypic",
        "Genotypic",
        "Test Reports",
        "Geneticists",
        "Genetic Counselors",
        "Inherited Condition",
        "Precision Medicine",
        "What is Genetic Testing",
        "DNA",
        "Genes",
        "Proteins",
        "Genetic Disorders",
        "Chromosomes",
        "Diagnostic",
        "Screening",
        "Risks and Limitations",
        "Medical Knowledge",
        "Technical Errors",
        "Mosaicism",
        "Positive",
        "Negative",
        "Inconclusive",
        "Variant of Uncertain Significance",
        "VUS",
        "Secondary Findings",
        "Incidental Findings",
        "ACMG",
        "Carrier Status",
        "Genetic Counseling",
        "NSGC",
        "Find a Genetic Counselor",
        "Confidentiality",
        "Privacy",
        ...resourcesKeywords
    ],

    // ============ ABOUT ============
    "/about": [
        "About AiLife",
        "About"
    ],
    "/about/people": [
        "Our People",
        "People",
        "Team"
    ],
    "/about/mission": [
        "Our Mission",
        "Mission"
    ],
    "/about/licenses": [
        "Our Licenses",
        "Licenses",
        "New York State Permit",
        "HiTrust",
        "HITRUST",
        "Clinical Laboratory Improvement Amendments",
        "CLIA",
        "College of American Pathologists",
        "CAP"
    ],
    "/about/publications": [
        "Our Publications",
        "Publications"
    ],
    "/about/cantact": [
        "Contact Us",
        "Contact"
    ],
    "/about/privacy": [
        "Privacy",
        "Privacy Policy"
    ],

    // ============ MEDIA ============
    "/media": [
        "News",
        "Blogs",
        "Media"
    ],
    "/media/news1": [
        "ASHG 2019 MEETING, HOUSTON",
        "ASHG",
        "Meeting",
        "Houston"
    ],
    "/media/news3": [
        "AiLife Diagnostics Achieves HITRUST Certification",
        "HITRUST",
        "Certification"
    ],
    "/media/news2": [
        "AILIFE DIAGNOSTICS AND BGI AMERICAS PARTNER TO PROVIDE CLINICAL WGS SOLUTION",
        "BGI",
        "WGS",
        "Clinical WGS Solution"
    ],
    "/media/blog1": [
        "Mappability in Calling CNVs in Exomes",
        "Mappability",
        "CNV",
        "Exomes"
    ],
    "/media/blog2": [
        "Public Perceptions of Genomic Data Sharing",
        "Genomic Data Sharing",
        "Data Sharing"
    ],
    "/media/blog3": [
        "Types of laboratory testing for COVID-19",
        "COVID-19",
        "Laboratory Testing"
    ],

    // ============ TOOLS ============
    "/tools": [
        "Tools"
    ],
    "/tools/AutoHPO": [
        "AutoHPO",
        "HPO",
        "Tools"
    ],
    "/tools/DiseaseRK": [
        "DiseaseRK",
        "Disease",
        "Tools"
    ],

    // ============ JOIN ============
    "/join": [
        // "We Are Hiring",
        "Hiring",
        "Careers",
        "Join"
    ]
};

function normalizePath(path) {
    let value = String(path || "");

    value = value.replace(/\/+/g, "/");

    if (value.length > 1 && value.endsWith("/")) {
        value = value.slice(0, -1);
    }

    return value || "/";
}

function joinPath(parentPath, childPath) {
    const parent = String(parentPath || "");
    const child = String(childPath || "");

    if (!child) {
        return normalizePath(parent || "/");
    }

    if (child.startsWith("/")) {
        return normalizePath(child);
    }

    if (!parent || parent === "/") {
        return normalizePath(`/${child}`);
    }

    return normalizePath(`${parent.replace(/\/$/, "")}/${child}`);
}

function getTitle(route) {
    if (route && route.meta && route.meta.title) {
        return route.meta.title;
    }

    if (route && route.name) {
        return route.name;
    }

    return "";
}

function getCategory(path) {
    if (path.startsWith("/test")) return "Tests";
    if (path.startsWith("/order")) return "Order";
    if (path.startsWith("/products")) return "Resources";
    if (path.startsWith("/about")) return "About";
    if (path.startsWith("/media")) return "News & Blogs";
    if (path.startsWith("/tools")) return "Tools";
    if (path.startsWith("/Services")) return "Services";
    if (path.startsWith("/clinical-case-examples")) return "Clinical Case Examples";
    if (path.startsWith("/join")) return "Careers";
    if (path.startsWith("/home")) return "Home";

    return "Site";
}

function getSectionKeywords(path) {
    if (path.startsWith("/test")) return testMenuKeywords;
    if (path.startsWith("/order")) return howToOrderKeywords;
    if (path.startsWith("/Services")) return industryResearchKeywords;

    return [];
}

function isParentRoute(route) {
    return Array.isArray(route.children) && route.children.length > 0;
}

const excludedSearchPaths = new Set([
    "/media",
    "/media/news1",
    "/media/news2",
    "/media/news3",
    "/media/blog1",
    "/media/blog2",
    "/media/blog3",
    "/about",
    "/about/people",
    "/about/publications",
    "/about/cantact"
]);

function shouldIncludeRoute(route, fullPath) {
    if (!route) return false;
    if (route.path === "*") return false;
    if (!getTitle(route)) return false;

    // 不让 Sample Collection Kit Request Form 出现在网站搜索结果中
    // 注意：这里只是从搜索中移除，不删除页面、不删除路由
    if (fullPath === "/order/kit") return false;
    if (excludedSearchPaths.has(fullPath)) return false;
    if (fullPath === "/join" || fullPath.startsWith("/join/")) return false;
    if (
        fullPath === "/clinical-case-examples" ||
        fullPath.startsWith("/clinical-case-examples/")
    ) return false;
    
    // 动态路由不要作为具体搜索结果，例如 /test/category/:type
    if (fullPath.includes(":")) return false;

    // 父级容器页面不直接作为搜索结果，避免跳转空容器
    if (isParentRoute(route)) return false;

    // redirect 不是最终页面
    if (route.redirect) return false;

    return true;
}

function uniqueArray(arr) {
    return Array.from(
        new Set(
            (arr || [])
                .filter(item => item !== undefined && item !== null)
                .map(item => String(item).trim())
                .filter(Boolean)
        )
    );
}

function flattenRoutes(routes, parentPath = "") {
    let list = [];

    (routes || []).forEach(route => {
        const fullPath = joinPath(parentPath, route.path);
        const title = getTitle(route);

        if (shouldIncludeRoute(route, fullPath)) {
            const category = getCategory(fullPath);
            const genes = uniqueArray(geneMap[fullPath] || []);
            const extraKeywords = uniqueArray(keywordMap[fullPath] || []);

            list.push({
                title,
                path: fullPath,
                category,
                keywords: uniqueArray([
                    title,
                    fullPath,
                    category,
                    ...getSectionKeywords(fullPath),
                    ...extraKeywords
                ]),
                genes
            });
        }

        if (route.children && route.children.length) {
            list = list.concat(flattenRoutes(route.children, fullPath));
        }
    });

    return list;
}

function dedupeSearchIndex(list) {
    const map = new Map();

    list.forEach(item => {
        const key = `${item.path}__${item.title}`;

        if (!map.has(key)) {
            map.set(key, item);
        }
    });

    return Array.from(map.values());
}

const supplementalSearchItems = [
    {
        title: "Clinical Specialties",
        path: "/test/category/audiology",
        category: "Tests",
        keywords: uniqueArray([
            "Clinical Specialties",
            "Clinical Specialty",
            "Clinical Categories",
            ...testMenuKeywords
        ]),
        genes: []
    },
    ...clinicalCategoryItems.map(item => ({
        title: item.title,
        path: item.path,
        category: "Tests",
        keywords: uniqueArray([
            item.title,
            "Clinical Specialties",
            ...testMenuKeywords
        ]),
        genes: []
    }))
];

const searchIndex = dedupeSearchIndex([
    ...flattenRoutes(router.options.routes),
    ...supplementalSearchItems
]);

export const hasGeneSearchData = searchIndex.some(item => {
    return Array.isArray(item.genes) && item.genes.length > 0;
});

export default searchIndex;



// WEBPACK FOOTER //
// ./src/utils/searchIndex.js